import {
    CheckCircleIcon,
    ExclamationTriangleIcon,
    XCircleIcon,
} from '@heroicons/react/24/outline';

import Text from '../../components/Text';
import type { EtatPermission } from '../../api/useAbonnementPush';
import { t } from '../../i18n/langue';

/**
 * Les lignes de diagnostic de l'écran « État des notifications » — extrait de
 * #21, #28, #54, #80. Voir `EtatNotifications.tsx` pour le contexte d'ensemble.
 */

type Niveau = 'ok' | 'attention' | 'casse' | 'inconnu';

const ICONES: Record<Niveau, typeof CheckCircleIcon> = {
    ok: CheckCircleIcon,
    attention: ExclamationTriangleIcon,
    casse: XCircleIcon,
    inconnu: CheckCircleIcon,
};

const TONS: Record<Niveau, string> = {
    ok: 'text-main-color',
    attention: 'text-amber-600',
    casse: 'text-alert-text-color',
    inconnu: 'text-text-color-light',
};

function Point({ niveau, libelle, detail }: { niveau: Niveau; libelle: string; detail?: string }) {
    const Icone = ICONES[niveau];

    return (
        <li className="flex items-start gap-3 px-5 py-3.5">
            <Icone aria-hidden="true" className={`mt-0.5 h-5 w-5 shrink-0 ${TONS[niveau]}`} />
            <div className="min-w-0">
                <Text variante="petit" className="font-medium">
                    {libelle}
                </Text>
                {detail && (
                    <Text variante="petit" ton="attenue" className="mt-0.5 block leading-snug">
                        {detail}
                    </Text>
                )}
            </div>
        </li>
    );
}

interface ListeDiagnosticProps {
    supporte: boolean;
    permission: EtatPermission | 'indisponible';
    enregistre: boolean | null;
    detailNonEnregistre: string;
    /** Le nom de l'établissement dont on reçoit les notifications, ou « — » s'il n'y en a pas. */
    nomFacilite: string;
    degrade: boolean;
    versionAffichee: { majeure: number; mineure: number } | null;
}

export default function ListeDiagnostic({
    supporte,
    permission,
    enregistre,
    detailNonEnregistre,
    nomFacilite,
    degrade,
    versionAffichee,
}: ListeDiagnosticProps) {
    return (
        <ul className="mx-4 mt-4 divide-y divide-border-color-light overflow-hidden rounded-2xl bg-white">
            <Point
                niveau={supporte ? 'ok' : 'casse'}
                libelle={t('notifications.diagnostic.installee')}
                detail={supporte ? undefined : t('notifications.diagnostic.ouvrezDepuisAccueil')}
            />

            <Point
                niveau={permission === 'granted' ? 'ok' : 'casse'}
                libelle={t('notifications.diagnostic.autorisees')}
                detail={
                    permission === 'denied'
                        ? t('notifications.diagnostic.refusee')
                        : permission === 'granted'
                          ? undefined
                          : t('notifications.diagnostic.pasRepondu')
                }
            />

            <Point
                niveau={enregistre === null ? 'attention' : enregistre ? 'ok' : 'casse'}
                libelle={t('notifications.diagnostic.enregistre')}
                detail={
                    enregistre === null
                        ? t('notifications.diagnostic.verificationEnCours')
                        : enregistre
                          ? t('notifications.diagnostic.serveurJoint')
                          : detailNonEnregistre
                }
            />

            <Point
                niveau="inconnu"
                libelle={t('notifications.diagnostic.dernierEnvoi')}
                detail={t('notifications.diagnostic.dernierEnvoiDetail')}
            />

            {/* La réserve du modèle « une sélection à la fois » (#20), rendue
                VISIBLE. Sans ce point, un manager posé sur l'hôtel B cesse de
                recevoir l'hôtel A sans que rien le lui dise. */}
            <Point niveau="ok" libelle={t('notifications.diagnostic.etablissement')} detail={nomFacilite} />

            {degrade && (
                <Point
                    niveau="attention"
                    libelle={t('notifications.diagnostic.versionIos', {
                        version: `${versionAffichee?.majeure}.${versionAffichee?.mineure}`,
                    })}
                    detail={t('notifications.diagnostic.versionIosDetail')}
                />
            )}
        </ul>
    );
}
